import { useEffect, useMemo, useState } from 'react'
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputAdornment,
  InputLabel,
  MenuItem,
  Radio,
  Select,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelIcon from '@mui/icons-material/Cancel'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { Alert, Tooltip } from '@mui/material'
import toast from 'react-hot-toast'
import { useIntl } from 'react-intl'
import Modal from '../../Unknown/Modal'
import ConfirmActionOtpModal from '../ConfirmActionOtpModal'
import translate from '../../../i18n/translate'
import { useAppDispatch, useAppSelector } from '../../../modules/store/hooks'
import { modalsSlice } from '../../../modules/store/reducers/modalsSlice'
import { eventApi, AdminEventLink } from '../../../modules/services/eventService'
import { smsAdminApi, EventSmsKind } from '../../../modules/services/smsAdminService'
import CustomPhoneInput from '../../UI/PhoneInput'

const LINK_REGEX = /^[A-Za-z0-9_-]+$/
const STATUSES: string[] = ['active', 'pending', 'hidden', 'canceled', 'finished', 'deleted']

type UserOption = { id: number; name: string; phone: string }

type Props = {
  event: { id: number; name?: string }
  onStatusChanged?: (nextStatus: string) => void
  onBaseLinkChanged?: (nextLink: string) => void
}

function extractErrorMessage(res: any): string | null {
  const candidates = [res?.data, res?.error?.data]
  for (const source of candidates) {
    if (!source) continue
    if (typeof source.message === 'string') return source.message
    if (typeof source.message?.message === 'string') return source.message.message
    if (typeof source.response?.message === 'string') return source.response.message
    if (Array.isArray(source.response?.message)) return source.response.message.join(', ')
    if (typeof source.error === 'string') return source.error
  }
  return null
}

const tabStyleSx = {
  '.MuiTab-root': { color: '#8b8b8b' },
  '.MuiTab-root.Mui-selected': { color: '#ED6C02' },
}

type EventLinkRowProps = {
  eventId: number
  item: AdminEventLink
  onSaved: (item: AdminEventLink, nextLink: string) => void
  onPreviousLinkDeleted: () => void
}

function EventLinkRow({ eventId, item, onSaved, onPreviousLinkDeleted }: EventLinkRowProps) {
  const intl = useIntl()
  const [changeEventLink, { isLoading: isSaving }] = eventApi.useChangeEventLinkMutation()
  const [deleteEventPreviousLink, { isLoading: isDeletingPrevious }] = eventApi.useDeleteEventPreviousLinkMutation()

  const [draft, setDraft] = useState<string>(item.link)
  const [touched, setTouched] = useState(false)
  const [debounced, setDebounced] = useState<string>(item.link)

  useEffect(() => {
    setDraft(item.link)
    setTouched(false)
  }, [item.link])

  useEffect(() => {
    const t = setTimeout(() => setDebounced(draft.trim()), 400)
    return () => clearTimeout(t)
  }, [draft])

  const trimmed = draft.trim()
  const invalid = !trimmed || !LINK_REGEX.test(trimmed)
  const changed = trimmed !== item.link
  const needsCheck = changed && !invalid

  const { currentData: availability, isFetching } = eventApi.useCheckEventLinkAvailabilityQuery(
    { id: eventId, linkId: item.id, link: debounced },
    { skip: !needsCheck || debounced !== trimmed }
  )

  // The check only counts once it has caught up with what is typed.
  const isChecking = needsCheck && (debounced !== trimmed || isFetching)
  const available = needsCheck && !isChecking && availability?.available === true
  const taken = needsCheck && !isChecking && availability?.available === false && availability?.reason === 'taken'

  const handleSave = async () => {
    setTouched(true)
    if (invalid || !changed) return

    const res: any = await changeEventLink({ id: eventId, linkId: item.id, link: trimmed })
    const errorMessage = extractErrorMessage(res)
    if (errorMessage) {
      const message = /already taken/i.test(errorMessage)
        ? intl.formatMessage({ id: 'eventSettingsBaseLinkTakenError' })
        : errorMessage
      toast.error(message)
      return
    }

    toast.success(intl.formatMessage({ id: 'eventSettingsLinkSuccess' }))
    onSaved(item, trimmed)
  }

  const handleDeletePrevious = async (previous: { id: number; link: string }) => {
    if (!window.confirm(`${previous.link}: ${intl.formatMessage({ id: 'eventSettingsLinkPreviousDeleteConfirm' })}`)) return

    const res: any = await deleteEventPreviousLink({ id: eventId, linkId: previous.id })
    const errorMessage = extractErrorMessage(res)
    if (errorMessage) {
      toast.error(errorMessage)
      return
    }

    toast.success(intl.formatMessage({ id: 'eventSettingsLinkPreviousDeleted' }))
    onPreviousLinkDeleted()
  }

  const previousLinks = item.previousLinks || []
  const { referralLink } = item
  const creator = referralLink?.creator
  const creatorLabel = creator
    ? `${creator.name || ''} ${creator.surname || ''} (${creator.phone || ''})`.trim()
    : ''
  const details = [creatorLabel, referralLink?.customRef ? `ref=${referralLink.customRef}` : ''].filter(Boolean).join(' · ')

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={1}
      p={1.5}
      sx={{ border: '1px solid #f0e6d8', borderRadius: 1, backgroundColor: '#fffaf3' }}
    >
      <Box>
        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
          <Typography variant="subtitle2">
            {item.isBase
              ? translate('eventSettingsSectionBaseLink')
              : referralLink?.name || translate('eventSettingsLinkOther')}
          </Typography>
          {referralLink && (
            <Chip
              size="small"
              variant="outlined"
              label={translate(referralLink.isPersonalLink ? 'eventSettingsLinkPersonal' : 'eventSettingsLinkReferral')}
            />
          )}
          {referralLink?.isDeleted && (
            <Chip size="small" color="error" variant="outlined" label={translate('eventSettingsLinkDeleted')} />
          )}
        </Box>
        {details && (
          <Typography variant="caption" color="textSecondary" sx={{ direction: 'ltr', display: 'block' }}>
            {details}
          </Typography>
        )}
      </Box>

      <Box display="flex" alignItems="flex-start" gap={1}>
        <TextField
          fullWidth
          size="small"
          color={available ? 'success' : 'warning'}
          variant="outlined"
          label={translate('eventSettingsLinkLabel')}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => setTouched(true)}
          disabled={isSaving}
          error={(touched && invalid) || taken}
          focused={available ? true : undefined}
          helperText={
            touched && invalid
              ? translate('eventSettingsBaseLinkInvalidError')
              : taken
                ? translate('eventSettingsBaseLinkTakenError')
                : available
                  ? translate('eventSettingsBaseLinkAvailable')
                  : translate('eventSettingsBaseLinkHelper')
          }
          FormHelperTextProps={{
            sx: available
              ? { color: (theme) => `${theme.palette.success.main} !important` }
              : undefined,
          }}
          InputLabelProps={{
            sx: available
              ? { color: (theme) => `${theme.palette.success.main} !important` }
              : undefined,
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {isChecking && <CircularProgress color="warning" size={16} />}
                {available && <CheckCircleIcon color="success" fontSize="small" />}
                {taken && <CancelIcon color="error" fontSize="small" />}
              </InputAdornment>
            ),
          }}
          sx={
            available
              ? {
                  '& .MuiOutlinedInput-root': {
                    color: (theme) => theme.palette.success.main,
                    '& fieldset': { borderColor: (theme) => theme.palette.success.main },
                    '&:hover fieldset': { borderColor: (theme) => theme.palette.success.main },
                    '&.Mui-focused fieldset': { borderColor: (theme) => theme.palette.success.main },
                  },
                }
              : undefined
          }
        />
        <Button
          color="warning"
          variant="contained"
          sx={{ flexShrink: 0, whiteSpace: 'nowrap', height: 40 }}
          disabled={isSaving || invalid || !changed || taken || isChecking}
          onClick={handleSave}
        >
          {isSaving ? <CircularProgress size={20} /> : translate('eventSettingsLinkSave')}
        </Button>
      </Box>

      {previousLinks.length > 0 && (
        <Box display="flex" alignItems="center" gap={0.75} flexWrap="wrap">
          <Typography variant="caption" color="textSecondary">
            {translate('eventSettingsLinkPrevious')}
          </Typography>
          {previousLinks.map((previous) => (
            <Chip
              key={previous.id}
              size="small"
              variant="outlined"
              label={previous.link}
              disabled={isDeletingPrevious}
              onDelete={() => handleDeletePrevious(previous)}
              sx={{ direction: 'ltr' }}
            />
          ))}
        </Box>
      )}
    </Box>
  )
}

export default function EventSettingsModal({ event, onStatusChanged, onBaseLinkChanged }: Props) {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const { toggleEventSettingsModal } = modalsSlice.actions
  const visible = useAppSelector((state) => state.modalsReducer.eventSettings)

  const { data: settings, isFetching, refetch } = eventApi.useGetEventSettingsQuery(
    { id: event?.id },
    { skip: !event?.id || visible !== 'open', refetchOnMountOrArgChange: true }
  )

  const [changeEventStatus, { isLoading: isSavingStatus }] = eventApi.useChangeEventStatusMutation()
  const [sendChangeEventOwnerOtp, { isLoading: isSendingOtp }] = eventApi.useSendChangeEventOwnerOtpMutation()
  const [changeEventOwner, { isLoading: isChanging }] = eventApi.useChangeEventOwnerMutation()

  const [tab, setTab] = useState<'common' | 'links' | 'owner' | 'sms'>('common')

  // SMS tab
  const [smsKind, setSmsKind] = useState<EventSmsKind>('resendBuyers')
  const [smsTextEn, setSmsTextEn] = useState('')
  const [smsTextHe, setSmsTextHe] = useState('')
  const [smsIsTestMode, setSmsIsTestMode] = useState(false)
  const [smsTestPhone, setSmsTestPhone] = useState('+972')
  const [smsTestUserName, setSmsTestUserName] = useState('')
  const [smsOtpOpen, setSmsOtpOpen] = useState(false)
  const [smsPendingIsTest, setSmsPendingIsTest] = useState(false)
  const [sendEventSmsOtp, { isLoading: isSendingEventSmsOtp }] = smsAdminApi.useSendEventSmsOtpMutation()
  const [confirmSendEventSms, { isLoading: isConfirmingEventSms }] = smsAdminApi.useConfirmSendEventSmsMutation()

  const smsUsesUserName = useMemo(
    () => smsTextEn.includes('%USER_NAME%') || smsTextHe.includes('%USER_NAME%'),
    [smsTextEn, smsTextHe]
  )

  // Common (status)
  const [statusDraft, setStatusDraft] = useState<string>('')

  // Event links
  const [linkSearch, setLinkSearch] = useState<string>('')
  const {
    currentData: linksData,
    isFetching: isFetchingLinks,
    refetch: refetchLinks,
  } = eventApi.useGetEventLinksQuery(
    { id: event?.id },
    { skip: !event?.id || visible !== 'open' || tab !== 'links', refetchOnMountOrArgChange: true }
  )

  const links = useMemo<AdminEventLink[]>(() => (Array.isArray(linksData) ? linksData : []), [linksData])

  const filteredLinks = useMemo(() => {
    const query = linkSearch.trim().toLowerCase()
    if (!query) return links

    return links.filter((item) =>
      [
        item.link,
        item.referralLink?.name,
        item.referralLink?.customRef,
        item.referralLink?.creator?.name,
        item.referralLink?.creator?.surname,
        item.referralLink?.creator?.phone,
        ...(item.previousLinks || []).map((previous) => previous.link),
      ].some((value) => (value || '').toLowerCase().includes(query))
    )
  }, [links, linkSearch])

  // Change owner
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [selectedUser, setSelectedUser] = useState<UserOption | null>(null)
  const [keepOldOwnerAsEditor, setKeepOldOwnerAsEditor] = useState(false)
  const [otpSent, setOtpSent] = useState(false)

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350)
    return () => clearTimeout(t)
  }, [search])

  useEffect(() => {
    if (visible === 'open' && settings) {
      setStatusDraft(settings.status || '')
    }
  }, [visible, settings])

  const skipUserSearch = !debouncedSearch || debouncedSearch.trim().length < 2 || visible !== 'open' || tab !== 'owner'
  const { data: usersData, isFetching: isSearching } = eventApi.useAdminSearchUsersQuery(
    { search: debouncedSearch.trim() },
    { skip: skipUserSearch }
  )

  const options = useMemo<UserOption[]>(() => {
    const list = Array.isArray(usersData) ? usersData : []
    return list.filter((u) => u.id !== settings?.creator?.id)
  }, [usersData, settings?.creator?.id])

  const statusChanged = statusDraft && settings && statusDraft !== settings.status

  const onCloseModal = () => {
    setTab('common')
    setStatusDraft('')
    setLinkSearch('')
    setSearch('')
    setDebouncedSearch('')
    setSelectedUser(null)
    setKeepOldOwnerAsEditor(false)
    setOtpSent(false)
    setSmsKind('resendBuyers')
    setSmsTextEn('')
    setSmsTextHe('')
    setSmsIsTestMode(false)
    setSmsTestPhone('+972')
    setSmsTestUserName('')
    setSmsOtpOpen(false)
    setSmsPendingIsTest(false)
    dispatch(toggleEventSettingsModal())
  }

  const isCustomSmsKind = smsKind === 'customBuyers' || smsKind === 'customHolders'
  const canSendEventSms = !isCustomSmsKind || Boolean(smsTextEn.trim() || smsTextHe.trim())

  const handleSendEventSmsOtp = async () => {
    if (isCustomSmsKind && !canSendEventSms) {
      toast.error(intl.formatMessage({ id: 'eventSmsValidationTextRequired' }))
      return
    }

    let normalizedTestPhone: string | undefined
    if (smsIsTestMode) {
      if (!isCustomSmsKind) return
      if (!smsTestPhone || smsTestPhone.length < 8) {
        toast.error(intl.formatMessage({ id: 'eventSmsTestPhoneRequired' }))
        return
      }
      normalizedTestPhone = smsTestPhone.startsWith('+') ? smsTestPhone : `+${smsTestPhone}`
      if (!normalizedTestPhone.startsWith('+972')) {
        toast.error(intl.formatMessage({ id: 'eventSmsIsraeliOnly' }))
        return
      }
      if (smsUsesUserName && !smsTestUserName.trim()) {
        toast.error(intl.formatMessage({ id: 'eventSmsTestUserNameRequired' }))
        return
      }
    }

    const res: any = await sendEventSmsOtp({
      id: event.id,
      kind: smsKind,
      textEn: smsTextEn.trim() || undefined,
      textHe: smsTextHe.trim() || undefined,
      testPhone: normalizedTestPhone,
      testUserName: smsIsTestMode ? smsTestUserName.trim() || undefined : undefined,
    })
    const errorMessage = extractErrorMessage(res)
    if (errorMessage) {
      toast.error(errorMessage)
      return
    }
    setSmsPendingIsTest(smsIsTestMode)
    setSmsOtpOpen(true)
    toast.success(intl.formatMessage({ id: 'eventSmsOtpSent' }))
  }

  const handleConfirmEventSms = async (finalCode: string) => {
    const res: any = await confirmSendEventSms({ id: event.id, code: finalCode })
    const errorMessage = extractErrorMessage(res)
    if (errorMessage) {
      toast.error(errorMessage)
      return
    }
    toast.success(
      smsPendingIsTest
        ? intl.formatMessage({ id: 'eventSmsTestSuccess' })
        : intl.formatMessage({ id: 'eventSmsBroadcastStarted' })
    )
    setSmsOtpOpen(false)
    setSmsTextEn('')
    setSmsTextHe('')
    setSmsIsTestMode(false)
    setSmsTestPhone('+972')
    setSmsTestUserName('')
  }

  const handleSaveStatus = async () => {
    if (!statusChanged) return

    const nextStatus = statusDraft
    const res: any = await changeEventStatus({ id: event.id, status: nextStatus })
    const errorMessage = extractErrorMessage(res)
    if (errorMessage) {
      toast.error(errorMessage)
      return
    }

    toast.success(intl.formatMessage({ id: 'changeEventStatusSuccess' }))
    if (onStatusChanged) onStatusChanged(nextStatus)
    refetch()
  }

  const handleLinkSaved = (item: AdminEventLink, nextLink: string) => {
    if (item.isBase && onBaseLinkChanged) onBaseLinkChanged(nextLink)
    refetchLinks()
  }

  const handleSendOtp = async () => {
    if (!selectedUser) return

    const res: any = await sendChangeEventOwnerOtp({
      id: event.id,
      newOwnerId: selectedUser.id,
      keepOldOwnerAsEditor,
    })

    const errorMessage = extractErrorMessage(res)
    if (errorMessage) {
      toast.error(errorMessage)
      return
    }

    setOtpSent(true)
    toast.success(intl.formatMessage({ id: 'changeEventOwnerOtpSent' }))
  }

  const handleConfirmOwner = async (finalCode: string) => {
    if (!selectedUser) return

    const res: any = await changeEventOwner({
      id: event.id,
      newOwnerId: selectedUser.id,
      keepOldOwnerAsEditor,
      code: finalCode,
    })

    const errorMessage = extractErrorMessage(res)
    if (errorMessage) {
      toast.error(errorMessage)
      return
    }

    onCloseModal()
    toast.success(intl.formatMessage({ id: 'changeEventOwnerSuccess' }))
  }

  const currentOwnerLabel = settings?.creator
    ? `${settings.creator.name || ''} ${settings.creator.surname || ''} (${settings.creator.phone || ''})`.trim()
    : ''

  return (
    <Modal
      title={translate('eventSettingsModalTitle')}
      visible={visible === 'open'}
      style={{ maxWidth: '820px' }}
      setVisible={onCloseModal}
    >
      <Box p={2} display="flex" flexDirection="column" gap={2}>
        {event?.name && (
          <Typography>
            <b>[{event.id}] {event.name}</b>
          </Typography>
        )}

        <Tabs
          value={tab}
          onChange={(_, next) => setTab(next)}
          textColor="secondary"
          TabIndicatorProps={{ style: { backgroundColor: '#ED6C02' } }}
          sx={tabStyleSx}
        >
          <Tab value="common" label={translate('eventSettingsTabCommon')} />
          <Tab value="links" label={translate('eventSettingsTabLinks')} />
          <Tab value="owner" label={translate('eventSettingsTabOwner')} />
          <Tab value="sms" label={translate('eventSettingsTabSms')} />
        </Tabs>

        {isFetching && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress color="warning" />
          </Box>
        )}

        {!isFetching && settings && tab === 'common' && (
          <Box display="flex" flexDirection="column" gap={2}>
            <Typography variant="body2" color="textSecondary">
              {translate('eventSettingsSectionStatus')}
            </Typography>
            <FormControl fullWidth size="small">
              <InputLabel color="warning">{translate('changeEventStatusLabel')}</InputLabel>
              <Select
                color="warning"
                value={statusDraft}
                label={translate('changeEventStatusLabel')}
                onChange={(e) => setStatusDraft(String(e.target.value))}
                disabled={isSavingStatus}
                sx={{ '& .MuiSelect-select': { textTransform: 'capitalize' } }}
              >
                {STATUSES.map((s) => (
                  <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>{s}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box display="flex" justifyContent="flex-end">
              <Button
                color="warning"
                variant="contained"
                disabled={!statusChanged || isSavingStatus}
                onClick={handleSaveStatus}
              >
                {isSavingStatus ? <CircularProgress size={20} /> : translate('eventSettingsSaveStatus')}
              </Button>
            </Box>
          </Box>
        )}

        {!isFetching && tab === 'links' && (
          <Box display="flex" flexDirection="column" gap={2}>
            <Alert severity="info" sx={{ py: 0.5 }}>
              {translate('eventSettingsLinksInfo')}
            </Alert>

            {isFetchingLinks && !linksData && (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress color="warning" />
              </Box>
            )}

            {linksData && !links.length && (
              <Typography variant="body2" color="textSecondary">
                {translate('eventSettingsLinksEmpty')}
              </Typography>
            )}

            {links.length > 5 && (
              <TextField
                fullWidth
                size="small"
                color="warning"
                variant="outlined"
                label={translate('eventSettingsLinksSearch')}
                value={linkSearch}
                onChange={(e) => setLinkSearch(e.target.value)}
              />
            )}

            {filteredLinks.length > 0 && (
              <Box display="flex" flexDirection="column" gap={1.5} sx={{ maxHeight: '50vh', overflowY: 'auto', pt: 0.5 }}>
                {filteredLinks.map((item) => (
                  <EventLinkRow
                    key={item.id}
                    eventId={event.id}
                    item={item}
                    onSaved={handleLinkSaved}
                    onPreviousLinkDeleted={refetchLinks}
                  />
                ))}
              </Box>
            )}
          </Box>
        )}

        {tab === 'sms' && (
          <Box display="flex" flexDirection="column" gap={2}>
            <Alert severity="info" sx={{ py: 0.5 }}>
              {translate('eventSmsIsraeliOnlyRemark')}
            </Alert>
            <Typography variant="body2" color="textSecondary">
              {translate('eventSmsTabDescription')}
            </Typography>

            <Box display="flex" flexDirection="column" gap={1}>
              {(['resendBuyers', 'notifyHolders', 'customBuyers', 'customHolders'] as EventSmsKind[]).map((kind) => {
                const isSelected = smsKind === kind
                return (
                  <Box
                    key={kind}
                    onClick={() => setSmsKind(kind)}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1,
                      p: 1.5,
                      cursor: 'pointer',
                      border: `1px solid ${isSelected ? '#ED6C02' : '#f0e6d8'}`,
                      backgroundColor: isSelected ? '#fff5e8' : '#fffaf3',
                      borderRadius: 1,
                    }}
                  >
                    <Radio color="warning" size="small" checked={isSelected} sx={{ p: 0, mt: 0.25 }} />
                    <Box flex={1}>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Typography variant="subtitle2">{translate(`eventSmsKind_${kind}_title`)}</Typography>
                        <Tooltip title={translate(`eventSmsKind_${kind}_tooltip`) as any}>
                          <InfoOutlinedIcon fontSize="small" color="warning" />
                        </Tooltip>
                      </Box>
                      <Typography variant="caption" color="textSecondary">
                        {translate(`eventSmsKind_${kind}_description`)}
                      </Typography>
                    </Box>
                  </Box>
                )
              })}
            </Box>

            {isCustomSmsKind && (
              <>
                <Alert severity="warning" icon={false} sx={{ py: 0.5 }}>
                  <Typography variant="caption">
                    {translate('eventSmsVariablesHelp')}
                  </Typography>
                </Alert>
                <Box display="flex" gap={1.5} sx={{ flexDirection: { xs: 'column', md: 'row' } }}>
                  <Box
                    flex={1}
                    display="flex"
                    flexDirection="column"
                    gap={1}
                    p={1.5}
                    sx={{ border: '1px solid #f0e6d8', borderRadius: 1, backgroundColor: '#fffaf3', direction: 'ltr' }}
                  >
                    <Typography variant="caption" color="textSecondary">EN</Typography>
                    <TextField
                      size="small"
                      variant="outlined"
                      color="warning"
                      label={translate('eventSmsTextEn')}
                      value={smsTextEn}
                      onChange={(e) => setSmsTextEn(e.target.value)}
                      disabled={isSendingEventSmsOtp || isConfirmingEventSms}
                      multiline
                      minRows={4}
                      maxRows={10}
                      inputProps={{ maxLength: 1000 }}
                    />
                  </Box>
                  <Box
                    flex={1}
                    display="flex"
                    flexDirection="column"
                    gap={1}
                    p={1.5}
                    sx={{ border: '1px solid #f0e6d8', borderRadius: 1, backgroundColor: '#fffaf3', direction: 'rtl' }}
                  >
                    <Typography variant="caption" color="textSecondary" sx={{ direction: 'ltr' }}>HE</Typography>
                    <TextField
                      size="small"
                      variant="outlined"
                      color="warning"
                      label={translate('eventSmsTextHe')}
                      value={smsTextHe}
                      onChange={(e) => setSmsTextHe(e.target.value)}
                      disabled={isSendingEventSmsOtp || isConfirmingEventSms}
                      multiline
                      minRows={4}
                      maxRows={10}
                      inputProps={{ maxLength: 1000 }}
                    />
                  </Box>
                </Box>
              </>
            )}

            {isCustomSmsKind && (
              <>
                <FormControlLabel
                  control={
                    <Checkbox
                      color="warning"
                      checked={smsIsTestMode}
                      onChange={(e) => setSmsIsTestMode(e.target.checked)}
                      disabled={isSendingEventSmsOtp || isConfirmingEventSms}
                    />
                  }
                  label={translate('eventSmsTestCheckbox')}
                />

                {smsIsTestMode && (
                  <Box display="flex" flexDirection="column" gap={1}>
                    <Alert severity="warning" icon={false} sx={{ py: 0.5 }}>
                      <Typography variant="caption">
                        {translate('eventSmsTestDefaultsHelp')}
                      </Typography>
                    </Alert>
                    <Box>
                      <Typography variant="caption" color="textSecondary">
                        {translate('eventSmsTestPhoneLabel')}
                      </Typography>
                      <CustomPhoneInput phone={smsTestPhone} setPhone={setSmsTestPhone} />
                    </Box>
                    {smsUsesUserName && (
                      <TextField
                        size="small"
                        variant="outlined"
                        color="warning"
                        label={translate('eventSmsTestUserNameLabel')}
                        helperText={translate('eventSmsTestUserNameHelper')}
                        value={smsTestUserName}
                        onChange={(e) => setSmsTestUserName(e.target.value)}
                        disabled={isSendingEventSmsOtp || isConfirmingEventSms}
                        inputProps={{ maxLength: 200 }}
                      />
                    )}
                  </Box>
                )}
              </>
            )}

            <Box display="flex" justifyContent="flex-end">
              <Button
                color="warning"
                variant="contained"
                disabled={isSendingEventSmsOtp || isConfirmingEventSms || !canSendEventSms}
                onClick={handleSendEventSmsOtp}
              >
                {isSendingEventSmsOtp ? (
                  <CircularProgress size={20} />
                ) : smsIsTestMode ? (
                  translate('eventSmsSendTest')
                ) : (
                  translate('eventSmsSend')
                )}
              </Button>
            </Box>
          </Box>
        )}

        {!isFetching && settings && tab === 'owner' && (
          <Box display="flex" flexDirection="column" gap={2}>
            <Box>
              <Typography variant="body2" color="textSecondary">
                {translate('changeEventOwnerCurrentOwner')}:
              </Typography>
              <Typography>
                <b>{currentOwnerLabel || '-'}</b>
              </Typography>
            </Box>

            <Autocomplete<UserOption>
              value={selectedUser}
              onChange={(_, next) => setSelectedUser(next)}
              inputValue={search}
              onInputChange={(_, next) => setSearch(next)}
              options={options}
              filterOptions={(x) => x}
              getOptionLabel={(option) => `${option.name || ''} (${option.phone || ''}) [id ${option.id}]`.trim()}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              loading={isSearching}
              noOptionsText={translate('changeEventOwnerNoOwnerSelected')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={translate('changeEventOwnerSearchLabel')}
                  helperText={translate('changeEventOwnerSearchHelper')}
                  color="warning"
                  variant="outlined"
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isSearching ? <CircularProgress color="warning" size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            <FormControlLabel
              control={
                <Switch
                  color="warning"
                  checked={keepOldOwnerAsEditor}
                  onChange={(e) => setKeepOldOwnerAsEditor(e.target.checked)}
                />
              }
              label={translate('changeEventOwnerKeepOldOwner')}
            />

            <Box display="flex" justifyContent="flex-end">
              <Button
                color="warning"
                variant="contained"
                disabled={!selectedUser || isSendingOtp}
                onClick={handleSendOtp}
              >
                {isSendingOtp ? <CircularProgress size={20} /> : translate('changeEventOwnerSendOtp')}
              </Button>
            </Box>
          </Box>
        )}
      </Box>

      <ConfirmActionOtpModal
        visible={otpSent}
        title={translate('changeEventOwnerConfirmModalTitle')}
        description={translate('changeEventOwnerConfirmModalDescription')}
        onClose={() => setOtpSent(false)}
        onConfirm={handleConfirmOwner}
        onResend={handleSendOtp}
        isConfirming={isChanging}
        isResending={isSendingOtp}
      />

      <ConfirmActionOtpModal
        visible={smsOtpOpen}
        title={translate('eventSmsOtpModalTitle')}
        description={translate('eventSmsOtpModalDescription')}
        onClose={() => setSmsOtpOpen(false)}
        onConfirm={handleConfirmEventSms}
        onResend={handleSendEventSmsOtp}
        isConfirming={isConfirmingEventSms}
        isResending={isSendingEventSmsOtp}
      />
    </Modal>
  )
}
